import React, { useCallback, useMemo, useState, useRef } from 'react';
import { observer } from 'mobx-react';
import { Popover, Skeleton } from 'antd';
import { UpOutlined, DownOutlined, SearchOutlined, PlusOutlined, InboxOutlined } from '@ant-design/icons';
import type { TreeDataNode, Tree } from 'antd';
import useIsMobile from 'hooks/use-is-mobile';
import {
  Container,
  TriggerContainer,
  IconContainer,
  TreeSelectorContainer,
  StyledTree,
  SearchContainer,
  StyledInput,
  FooterContainer,
  TreeNodeContainerBase,
  TreeNodeContainerLink,
  AddButton,
  TreeNodeTitle,
  FooterButton,
  SkeletonContainer,
  NoDataContainer
} from 'components/common/popover-tree-selector/popover-tree-selector.utils';
import { useFilteredTreeData } from 'components/common/popover-tree-selector/use-filtered-tree-data';
import { usePopoverTreeScroll } from 'components/common/popover-tree-selector/use-popover-tree-scroll';
import { usePopoverTreeExpansion } from 'components/common/popover-tree-selector/use-popover-tree-expansion';
import { useTheme } from 'hooks/use-theme';
import { intl } from 'components/localization';
import { getParentMap } from 'components/common/popover-tree-selector/tree-utils';

interface PopoverTreeSelectorProps {
  treeData: TreeDataNode[];
  selectedKeys: React.Key[];
  onSelect: (selectedKeys: React.Key[]) => void;
  showSearch?: boolean;
  searchPlaceholder: string;
  footerTitle?: string;
  onFooterClick?: () => void;
  headerContent?: React.ReactNode;
  trigger?: React.ReactNode;
  onAddClick?: (key: React.Key) => void;
  isLoading?: boolean;
  dataE2e: string;
  getNodeUrl?: (key: React.Key) => string;
}

const PopoverTreeSelector = ({
  treeData,
  selectedKeys,
  onSelect,
  showSearch,
  searchPlaceholder,
  footerTitle,
  onFooterClick,
  headerContent,
  onAddClick,
  isLoading,
  trigger,
  dataE2e,
  getNodeUrl
}: PopoverTreeSelectorProps) => {
  const { isMobile } = useIsMobile();
  const theme = useTheme();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const treeRef = useRef<React.ElementRef<typeof Tree>>(null);

  const closePopover = useCallback(() => {
    setIsPopoverOpen(false);
    setSearchValue('');
  }, [setIsPopoverOpen, setSearchValue]);

  const handleTreeSelect = useCallback(
    (keys: React.Key[]) => {
      if (keys.length === 0 && selectedKeys.length > 0) {
        return;
      }
      onSelect(keys);
      closePopover();
    },
    [selectedKeys, onSelect, closePopover]
  );

  const handleAddClick = useCallback(
    (nodeKey: React.Key, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onAddClick?.(nodeKey);
      closePopover();
    },
    [onAddClick, closePopover]
  );

  const footerClick = useCallback(() => {
    onFooterClick?.();
    closePopover();
  }, [onFooterClick, closePopover]);

  const renderTreeNodeTitle = useCallback(
    (nodeData: TreeDataNode, originalTitle: React.ReactNode) => {
      const nodeUrl = getNodeUrl?.(nodeData.key!);

      const content = (
        <>
          <TreeNodeTitle>{originalTitle}</TreeNodeTitle>
          {onAddClick && (nodeData as any).canCreate && (
            <AddButton onClick={e => handleAddClick(nodeData.key!, e)} data-e2e={`tree-node-add-${nodeData.key}`}>
              <PlusOutlined />
            </AddButton>
          )}
        </>
      );

      if (nodeUrl) {
        return (
          <TreeNodeContainerLink to={nodeUrl} data-e2e={`tree-node-title-${nodeData.key}`}>
            {content}
          </TreeNodeContainerLink>
        );
      }

      return <TreeNodeContainerBase data-e2e={`tree-node-title-${nodeData.key}`}>{content}</TreeNodeContainerBase>;
    },
    [onAddClick, handleAddClick, getNodeUrl]
  );

  const { filteredTreeData, searchMatchKeys } = useFilteredTreeData({
    treeData,
    searchValue,
    showSearch,
    renderTreeNodeTitle
  });

  const parentMap = useMemo(() => getParentMap(treeData), [treeData]);

  const { expandedKeys, handleExpand } = usePopoverTreeExpansion({
    isPopoverOpen,
    selectedKey: selectedKeys?.[0],
    searchMatchKeys,
    parentMap
  });

  usePopoverTreeScroll({
    isPopoverOpen,
    scrollToKey: selectedKeys?.[0],
    parentMap,
    treeRef
  });

  if (isLoading) {
    return (
      <SkeletonContainer data-e2e={`${dataE2e}-skeleton`}>
        <Skeleton.Input style={{ width: 60, height: 16 }} active />
      </SkeletonContainer>
    );
  }

  const popoverContent = (
    <TreeSelectorContainer>
      {headerContent}
      {showSearch && (
        <SearchContainer>
          <StyledInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            data-e2e="search-input"
          />
        </SearchContainer>
      )}
      {filteredTreeData.length === 0 ? (
        <NoDataContainer>
          <InboxOutlined />
          <div>{intl.formatMessage({ id: 'popover-tree-selector.empty' })}</div>
        </NoDataContainer>
      ) : (
        <StyledTree
          ref={treeRef}
          treeData={filteredTreeData}
          onSelect={handleTreeSelect}
          selectedKeys={selectedKeys}
          blockNode
          expandedKeys={expandedKeys}
          onExpand={handleExpand}
        />
      )}
      {footerTitle && (
        <FooterContainer>
          <FooterButton onClick={footerClick} data-e2e="footer-button">
            {footerTitle}
          </FooterButton>
        </FooterContainer>
      )}
    </TreeSelectorContainer>
  );

  return (
    <Container>
      <Popover
        content={popoverContent}
        trigger="click"
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement={isMobile ? 'bottom' : 'bottomRight'}
        overlayInnerStyle={{
          background: theme.appTheme.primaryGray,
          padding: 0
        }}>
        <TriggerContainer>
          {trigger}
          <IconContainer data-e2e={dataE2e}>
            <UpOutlined />
            <DownOutlined />
          </IconContainer>
        </TriggerContainer>
      </Popover>
    </Container>
  );
};

export default observer(PopoverTreeSelector);
